<template>
  <div class="order-container">
    <el-header>
      <div class="header-content">
        <el-button 
          icon="el-icon-back" 
          @click="goBack" 
          class="back-button"
        >
          Back
        </el-button>
        <h1>Order Details</h1>
        <div class="header-spacer"></div>
      </div>
    </el-header>
    <el-main>
      <el-card class="order-card">
        <div class="collection-preview">
          <div class="cover-image">
            <img v-if="collection.cover_url" :src="collection.cover_url" alt="Collection Cover">
            <div v-else class="placeholder-cover">
              <i class="el-icon-picture-outline"></i>
            </div>
          </div>
          <div class="collection-info">
            <h2>{{ collection.title }}</h2>
            <p class="author">By {{ collection.user_name }}</p>
            <div class="collection-stats">
              <span><i class="el-icon-folder"></i> {{ collection.group_count || 0 }} Groups</span>
              <span><i class="el-icon-document"></i> {{ collection.knowit_count || 0 }} Knowits</span>
              <span><i class="el-icon-reading"></i> {{ collection.content_count || 0 }} Contents</span>
            </div>
          </div>
        </div>

        <div class="order-details">
          <div class="price-details">
            <div class="price-row">
              <h3>Price</h3>
              <div class="price-amount">
                <span>FREE</span>
              </div>
            </div>
            <div class="price-row total-row">
              <h3>Total</h3>
              <div class="price-amount total-price">
                <span>$0.00</span>
              </div>
            </div>
          </div>

          <div class="order-summary">
            <h3>Order Summary</h3>
            <div class="summary-item">
              <span>Order Time</span>
              <span>{{ formatDate(new Date()) }}</span>
            </div>
            <div class="summary-item">
              <span>Order Status</span>
              <el-tag size="small" type="warning">Pending Payment</el-tag>
            </div>
          </div>

          <div class="payment-section">
            <el-button 
              type="primary" 
              :loading="creating" 
              @click="createOrder"
              style="width: 100%"
            >
              Confirm and Claim Free
            </el-button>
            <p class="payment-note">
              <span>By proceeding, you agree to our <a href="#">User Policy</a> and <a href="#">Terms of Service</a>. Requires active platform subscription.</span>
            </p>
          </div>
        </div>
      </el-card>
    </el-main>
  </div>
</template>

<script>
import { postRequest } from '@/apis/front'

export default {
  name: 'CollectionOrder',
  data() {
    return {
      collection: {},
      creating: false
    }
  },
  created() {
    this.fetchCollectionData()
  },
  beforeDestroy() {
    // Clean up the temporary collection ID from store
    this.$store.dispatch('setTempCollectionId', null)
  },
  methods: {
    formatDate(date) {
      return new Date(date).toLocaleString()
    },
    async fetchCollectionData() {
      try {
        const collectionId = this.$store.state.tempCollectionId || this.$route.query.collection_id
        
        if (!collectionId) {
          this.$message.error('Collection ID not found')
          this.$router.go(-1)
          return
        }
        
        const response = await postRequest('getPostCollection', {
          id: collectionId,
          user_id: this.$store.state.user_id
        })
        
        if (response.data.code === 0) {
          this.collection = response.data.data
        } else {
          this.$message.error(response.data.msg || 'Failed to load collection')
        }
      } catch (error) {
        this.$message.error('Failed to load collection')
      }
    },
    async createOrder() {
      // Check subscription before claiming
      const subscription = this.$store.state.platformSubscription
      if (!subscription || !subscription.active) {
        this.$confirm(
          'An active platform subscription is required to claim content. Would you like to subscribe now?',
          'Subscription Required',
          {
            confirmButtonText: 'Subscribe Now',
            cancelButtonText: 'Cancel',
            type: 'warning'
          }
        ).then(() => {
          this.$router.push('/platform-subscription')
        }).catch(() => {})
        return
      }

      this.creating = true
      try {
        const collectionId = this.$store.state.tempCollectionId || this.$route.query.collection_id
        const response = await postRequest('createCollectionOrder', {
          user_id: this.$store.state.user_id,
          collection_id: collectionId,
          usd_price: this.collection.usd_price
        })
        
        if (response.data.code === 0 && response.data.data.status === 'PENDING') {
          this.$message.success('Order created successfully')
          // Redirect to payment page with order ID
          // this.$router.push('/my-collections')
          this.$router.push({
            path:'/pay',
            query:{
              order_id:(response.data.data._id.$oid || response.data.data._id.$id),
              order_type:"collection"
            }
          })
        } else if (response.data.code === -2 ||(response.data.code ===0 && response.data.data.status === 'COMPLETE')) {
          this.$message.warning('You have already purchased this course')
          this.$store.dispatch('setTempCollectionId', collectionId)
          this.$router.push({ path: '/deck', query: { id: collectionId } })
        } else {
          throw new Error(response.data.msg || 'Failed to create order')
        }
      } catch (error) {
        this.$message.error(error.message || 'Failed to create order')
      } finally {
        this.creating = false
      }
    },
    goBack() {
      this.$router.go(-1)
    }
  }
}
</script>

<style scoped>
.order-container {
  width: 100%;
}

.el-header {
  background-color: #409EFF;
  color: white;
  padding: 0;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.header-content {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  justify-content: space-between;
  height: 100%;
}

.order-card {
  width: 70%;
  margin: 20px auto;
  padding: 30px;
}

.collection-preview {
  display: flex;
  gap: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #EBEEF5;
}

.cover-image {
  width: 150px;
  height: 212px;
  border-radius: 8px;
  overflow: hidden;
  background-color: #f5f7fa;
}

.cover-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.collection-info {
  flex: 1;
}

.collection-info h2 {
  margin: 0 0 10px 0;
}

.author {
  color: #606266;
  margin-bottom: 15px;
}

.collection-stats {
  display: flex;
  gap: 20px;
}

.collection-stats span {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #606266;
}

.order-details {
  padding-top: 20px;
}

.price-details {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #fafafa;
}

.price-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #e0e0e0;
}

.price-row:last-child {
  border-bottom: none;
}

.price-row h3 {
  margin: 0;
  font-size: 16px;
  color: #616161;
}

.price-amount {
  font-size: 16px;
  font-weight: 500;
  color: #212121;
}

.total-row h3,
.total-row .total-price {
  font-weight: 700;
  font-size: 18px;
  color: #212121;
}

.order-summary {
  margin-bottom: 30px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #EBEEF5;
}

.payment-section {
  text-align: center;
}

.payment-note {
  margin-top: 10px;
  color: #909399;
  font-size: 12px;
}

.back-button {
  position: absolute;
  left: 20px;
  color: white;
  border: 1px solid white;
  background: transparent;
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border-color: white;
}

.header-content h1 {
  flex: 1;
  text-align: center;
  font-size: 2rem;
  font-weight: bold;
  margin: 0;
  color: white;
  line-height: 72px;
}

.payment-methods {
  margin-top: 20px;
}
</style> 